/**
 * Spanish (es) translation.
 * Must implement EXACTLY the same shape as `pt.ts` (enforced by `Translations` type).
 */
import type { Translations } from '..';

const es: Translations = {
  meta: {
    htmlLang: 'es',
  },
  common: {
    loading: 'Cargando...',
    next: 'Siguiente',
    finish: 'Finalizar',
    continue: 'Continuar',
    submit: 'Pagar',
    done: 'Listo',
    copy: 'COPIAR',
    download: 'Descargar PDF',
    requiredField: '¡Campo obligatorio!',
    invalidField: '¡Campo inválido!',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Portugués',
    en: 'Inglés',
    es: 'Español',
  },
  errors: {
    pixExpired: '¡El Pix expiró! Realiza una nueva compra.',
    purchaseFailed: 'Falla en la compra',
    portabilityFailed: 'Fallo en la solicitud de portabilidad',
    portabilityUndefined: 'Fallo en la solicitud de portabilidad: respuesta indefinida',
    portabilityNotScheduled: 'No pudimos programar tu portabilidad. Contáctanos por WhatsApp para completarla.',
    cardRegisterUnknown: 'Error desconocido al registrar la tarjeta',
    purchaseUnknown: 'Error en la solicitud de compra',
    invalidCoupon: '¡Ingresa un cupón válido!',
    cepNotFound: 'Código postal no encontrado. Verifica los dígitos.',
    generic: 'No fue posible procesar la solicitud. Inténtalo de nuevo.',
    noConnection: 'Fallo de conexión. Verifica tu internet e inténtalo de nuevo.',
    byStatus: {
      400: 'Solicitud inválida.',
      401: 'Sesión no autenticada. Recarga la página.',
      402: 'Pago rechazado. Verifica los datos de la tarjeta o prueba con otra.',
      403: 'Acceso denegado.',
      404: 'Recurso no encontrado.',
      405: 'Operación no permitida.',
      409: 'Esta operación ya no está disponible.',
      422: 'Los datos informados son inválidos o incorrectos.',
      429: 'Demasiados intentos. Espera unos instantes.',
      500: 'Error interno del servidor. Inténtalo de nuevo.',
      502: 'Servicio temporalmente no disponible. Inténtalo de nuevo.',
      503: 'Servicio temporalmente no disponible. Inténtalo de nuevo.',
    },
    retryAfter: 'Espera {{segundos}} segundos e inténtalo de nuevo.',
    paymentDeclined: 'Pago rechazado. Verifica los datos de la tarjeta.',
    paymentUnavailable: 'El pago no está disponible temporalmente. Inténtalo más tarde.',
    paymentAttemptsOver: 'Sesión agotada por demasiados intentos de pago.',
    purchaseExpired: 'Esta compra expiró o no fue validada. Realiza la compra de nuevo.',
    summaryUnavailable: 'No fue posible cargar el resumen de la compra.',
    planUnavailable: 'El plan elegido ya no está disponible. Elige otro plan para continuar.',
    threeDSTimeout: 'La autenticación de tu banco no respondió. Inténtalo de nuevo — los datos de tu compra siguen siendo válidos.',
    threeDSUnavailable: 'No pudimos cargar la verificación de seguridad del pago. Recarga la página; si el problema continúa, contáctanos por el correo ti@conteltelecom.com.br.',
    threeDSDeclined: 'Tu banco rechazó la autenticación 3DS. Prueba con otra tarjeta.',
    threeDSTechnicalError: 'Error técnico en la autenticación 3DS. Inténtalo de nuevo.',
    threeDSStartFailed: 'No fue posible iniciar la autenticación 3DS. Recarga la página.',
    threeDSUnenrolled: 'No pudimos autenticar esta tarjeta con tu banco. Verifica los datos o usa otra tarjeta.',
    threeDSUnsupportedBrand: 'La marca de esta tarjeta no es compatible con la autenticación. Usa una tarjeta de otra marca.',
    recaptchaFailed: 'Error en la validación del reCAPTCHA. ¡Inténtalo de nuevo!',
  },
  snackbar: {
    pixCopied: '¡Pix copiado!',
    codeCopied: '¡Código copiado!',
  },
  page404: {
    notFound: 'Página no encontrada',
  },
  page1: {
    sloganDefault: 'Potencia tu celular con la mayor velocidad',
    uncomplicateDefault: 'Simplifica',
    detailsDefault1: 'Únete a más de 123 mil usuarios',
    detailsDefault2: 'Tenemos el plan perfecto para ti',
    bestSellersDefault1: 'Sé como los mejores',
    bestSellersDefault2: 'Elige un plan que sea tu estilo',
    chooseProfile1: 'Tú eliges tu perfil',
    chooseProfile2: 'Hecho para ti, a tu medida',
    perfilLabel: 'PERFIL',
    choosePlanTitle: 'Elige tu plan',
    choosePlanSubtitle: 'Tú decides cuánta internet necesitas',
    chipAvulsoFallback: 'individual',
    chipAvulsoDescription: 'Compra el chip ahora y activa tu plan después',
    bnftsTitle: 'Beneficios del Chip',
    whatsappFreeHtml: 'Whatsapp<br/> Gratis',
    callsInfiniteHtml: 'Llamadas<br/> Infinitas',
    wazeUnlimitedHtml: 'Waze<br/> Ilimitado',
    activatedBnfts: {
      title: 'Beneficios Activos',
      portabilityActivated: 'Portabilidad Activa',
      moreInternet: 'Más internet por el mismo precio',
      bonusGB: '+5GB Gratis',
    },
    planDetails: {
      whatsapp: 'WhatsApp incluido',
      callsUnlimited: 'Llamadas ilimitadas',
      callsLimited: '60min de llamadas',
      net5G: 'Red 5G',
      internetAccumulate: 'Internet que se acumula',
      noCutsSuffix: 'sin cortes',
      nationalCoverage: 'Cobertura nacional',
      wazeUnlimited: 'Waze ilimitado',
      mobileIncluded: 'Incluido en tu plan',
      mobileTechnology: 'Tecnología',
      mobileAccumulate: 'Acumula',
      mobileInternetAccumulation: 'Acumulación de internet',
      mobileNoCuts: 'Sin Cortes',
      mobileInternet: 'Internet',
      mobileBonus: 'Bono',
      mobileMonth: 'Cada mes',
    },
    selectPlanDetails: {
      portabilityOn: 'Traer mi número de otra operadora',
      portabilityOff: 'Traer mi número de otra operadora',
      gainingInternet: 'Estás ganando más internet',
      losingInternet: 'Estás perdiendo internet',
      losingBadge: 'PIERDES 5GB',
      gainingBadge: 'GANAS 5GB',
      mostPopular: 'MÁS ELEGIDO',
      renewMonthly: 'Renueva cada mes sin permanencia',
      mostProfitable: 'MÁS RENTABLE',
      buyTwelveForEight: 'Paga por 8 planes y obtén 12',
      chipPhysical: 'Chip físico',
      becomeClient: 'Para volverte cliente',
      becomeClientFallback: 'Para volverte nuestro cliente',
      monthly: 'Mensual',
      annual: 'Anual',
    },
    invite: {
      inviteFrom: 'Recibiste una invitación de',
      toBePartOf: 'para ser parte de',
      configureYourPlan: 'CONFIGURA TU PLAN',
    },
    voucher: {
      iHaveCode: 'Tengo un código',
      brandClub: '{{brand}} Club',
      digitCode: 'Escribe el código de invitación',
      placeholder: '#FE2025',
      validate: 'VALIDAR',
      invalidCode: '¡Escribe un cupón válido!',
      validCode: '¡Cupón válido!',
    },
    timer: {
      limitedTime: 'Tiempo Limitado',
      gainBonus: 'Gana 5 GB trayendo tu número',
    },
    slider: {
      internetAmount: 'Cantidad de Internet',
    },
  },
  page2: {
    headingDefaultTitles: {
      step0: 'Configura tu Plan',
      step0Sub: 'Elige más opciones y mejora tu plan',
      formStep: 'Información Personal',
      formStepSub: 'Completa los campos obligatorios',
      paymentStep: 'Elige cómo pagar',
      paymentStepSub: 'Elige más opciones y mejora tu plan',
      step4: 'Pago',
      step4Sub: 'Completa el pago de tu compra',
    },
    stepper: {
      yourPlan: 'Tu Plan',
      info: 'Información',
      address: 'Dirección',
      paymentMethod: 'Método de pago',
      payment: 'Pago',
    },
    chipInfo: {
      physicalChipHtml: 'Chip Físico <span>(Compra Única)</span>',
      physicalChipDesc: 'Chip que insertas en tu dispositivo',
      receiveAtHome: 'Recibir en casa',
      receiveAtHomeDesc: 'Recibe el chip en tu dirección',
      pickUpInStore: 'Retirar en tienda',
      pickUpInStoreDesc: 'Retira el chip en la tienda más cercana',
      virtualChip: 'Chip Virtual',
      virtualChipDesc: 'Chip para celulares modernos (ej. iPhone 12)',
    },
    pickUpChip: {
      atHome: 'Recibir en casa',
      atHomeDesc: 'Recibe el chip en tu dirección',
      atStore: 'Retirar en tienda',
      atStoreDesc: 'Ver en el mapa',
    },
    sectionTitles: {
      chipReceive: 'Recibimiento del chip físico',
      chipReceiveSub: 'Elige cómo llegará tu chip',
      accessTechnology: 'Tecnología de acceso',
      accessTechnologySub: 'Cómo te vas a conectar',
      bonusGain: 'Gana Bono',
      bonusGainSub: 'Mira cómo aumentar tu internet',
    },
    smartControl: {
      title: 'Contrato Inteligente',
      subtitle: 'Mismo precio con más internet',
      bonusBadge: '+5GB GRATIS',
    },
    personalInfo: {
      title: 'Información Personal',
      subtitle: 'Cómo podemos identificarte',
      fullName: 'Nombre Completo',
      cpfCnpj: 'ID Fiscal',
      birthdate: 'Fecha de Nacimiento',
    },
    contactInfo: {
      title: 'Información de Contacto',
      subtitle: 'Cómo podemos contactarte',
      email: 'Correo',
      ddd: 'Código de área',
      phone: 'Teléfono',
    },
    addressInfo: {
      title: 'Información de Dirección',
      subtitle: 'Dónde podemos encontrarte',
      cep: 'Código Postal',
      state: 'Estado',
      city: 'Ciudad',
      street: 'Calle',
      neighborhood: 'Barrio',
      number: 'Número',
      complement: 'Complemento',
      reference: 'Referencia',
    },
    payment: {
      oneTimeTitle: 'Pago al contado',
      oneTimeSubtitle: 'Selecciona una de las opciones de pago al contado',
      installmentTitle: 'Pago en cuotas',
      installmentSubtitle: 'Paga en cuotas con tarjeta de crédito',
      installmentSubtitleUnixcard: 'Paga en hasta 12 cuotas',
      finalizeInstallmentTitle: 'Finaliza tu compra en cuotas',
      finalizeInstallmentSubtitle: 'Haz clic en "Pagar" y serás redirigido para completar el pago.',
      cardTitle: 'Tarjeta de Crédito',
      cardSubtitle: 'Aceptamos todas las tarjetas',
      cardBadge: 'RECOMENDADO',
      cardNumber: 'Número de Tarjeta',
      cardName: 'Nombre Completo',
      cardCpf: 'ID Fiscal del Titular',
      cardExpiry: 'Fecha de Vencimiento',
      cardCvv: 'Código de Seguridad',
      keepRecurring: 'Mantener recurrencia en la tarjeta de crédito',
      pixTitle: 'PIX',
      pixSubtitle: 'Paga con tu app bancaria',
      pixBadge: 'PAGO INMEDIATO',
      pixStep1Title: 'Abre tu app bancaria',
      pixStep1Desc: 'Puedes pagar con el banco de tu preferencia.',
      pixStep2Title: 'Escanea o escribe la clave PIX',
      pixStep2Desc: 'Apunta la cámara de tu celular a la imagen al lado o copia el código',
      pixStep3Title: '¡Listo! Espera la confirmación',
      pixStep3Desc: 'Una vez confirmado serás dirigido a la siguiente página',
      pixExpiresIn: 'EXPIRA EN: ',
      pixTimeRemaining: 'Tiempo restante',
      boletoTitle: 'Boleto Bancario',
      boletoSubtitle: 'Hasta 2 días hábiles',
      boletoBadge: 'Paga con boleto',
      boletoStep1Title: 'Copia el código y paga con tu banco',
      boletoStep1Desc: 'Puedes pagar con el banco de tu preferencia.',
      wppFinalizeTitle: '¡Finaliza el pago por WhatsApp!',
      wppFinalizeDesc: 'Haz clic en "Continuar" y finaliza el pedido con uno de nuestros agentes.',
      paymentApproved: '¡Pago aprobado! Redirigiendo...',
      unixCardDescription: 'Usa tu tarjeta Unix para pagar la compra',
      unixCardInstallmentDescription: 'Paga en hasta 12 cuotas con tu tarjeta Unix',
      cardInstallmentDescription: 'Serás redirigido a nuestro sitio de pago',
      installments: 'Cuotas',
      installmentOption: '{{n}}x de {{valor}}',
      redoPurchase: 'Rehacer compra',
      paymentDeadlineExpired: 'El plazo para el pago expiró. Rehaz la compra.',
      payWithin: 'Completa el pago en {{tempo}}',
    },
    summary: {
      total: 'Total de esta compra',
      orderTitle: 'Resumen de tu pedido',
      orderSubtitle: 'Detalles de tu pedido',
      secondTitle: 'Resumen de tu pedido',
      secondSubtitle: 'Todo lo que está incluido en tu pedido con nosotros',
      bonusTitle: 'Bono del Plan',
      bonusSubtitle: 'Gana más internet en tu plan',
      portability: 'Portabilidad',
      portabilityDesc: 'Cambia de operadora y mantén tu número',
      portabilityBonus: '+5GB GRATIS',
      smartControl: 'Control Inteligente',
      smartControlDesc: 'Pago mensual con permanencia de 12 meses',
      chipPhysical: 'Chip Físico',
      chipVirtual: 'Chip Virtual',
      chipValue: 'Valor del Chip',
      chipPaymentOnce: 'Pago Único',
      chipDigitalDelivery: 'Entrega Digital',
      shipping: 'Envío',
      shippingDesc: 'Envío inmediato',
      paymentMonthly: 'Pago mensual',
      paymentOneTime: 'Pago al contado',
      paymentInstallment: 'Pago en cuotas',
      chipUnit: 'chip',
      chipUnitPlural: 'chips',
    },
    coupon: {
      title: 'Descuentos',
      remove: 'Quitar',
      active: 'Cupón activo',
      codeLabel: 'Código de Descuento',
      activate: 'Activar',
      activateLong: 'Activar Cupón',
    },
  },
  page3: {
    welcomeGreeting1: 'Bienvenido a',
    welcomeGreeting2: 'Connect,',
    welcomeDescription: '¡Ahora formas parte de la revolución de la comunicación! Disfruta de tu nuevo plan.',
    nextStepsTitle: 'Próximos Pasos',
    nextStepsSubtitle: 'Termina de configurar tu cuenta aquí',
    plan: {
      monthlyTitle: 'Tu Plan Mensual',
      annualTitle: 'Tu Plan Anual',
      monthlyDesc: 'Mira cuánta internet mensual tienes',
      smartControlBadge: '+5GB CONTRATO',
      portabilityBadge: '+5GB PORTABILIDAD',
    },
    stepper: {
      whatsappHelp: 'Necesito ayuda',
      esim: 'eSIM',
      installEsim: 'Instalación del eSIM',
      installEsimDesc: 'Abre el PDF abajo para iniciar la configuración',
      portabilityTitle: 'PORTABILIDAD',
      schedulePortability: 'Agendar Portabilidad',
      portabilityWarning: 'Tranquilo, el proceso solo inicia cuando tu chip Contel sea activado.',
      whichPhoneTransfer: '¿Qué número de teléfono se debe transferir a Contel?',
      whichOperator: '¿De qué operadora es actualmente este número?',
      confirmEverything: 'Confirma que todo está correcto',
      brandFallback: 'Connect',
      leaving: 'Saliendo',
      entering: 'Entrando',
      attention: 'Atención',
      attentionText: 'Al finalizar confirmo que este número está activo y registrado en mi ID fiscal',
      allDone: 'Todo listo',
      portabilityProcessNote: 'En 3 días hábiles después de la confirmación del pedido de portabilidad, tu número será migrado.',
      esimInstructions: 'Haz clic en Finalizar para confirmar tu solicitud de portabilidad',
      chipInstructions: 'Cuando llegue tu CHIP Físico, actívalo normalmente',
      followingSmsConfirm: 'Después, responde SÍ al SMS de confirmación que llegará en breve',
      buildYourPlan: 'Armar tu plan',
      buildYourPlanDone: 'Plan completado con éxito',
      installEsimTitle: 'Instalar eSIM',
      installEsimComplete: 'Completa la instalación del CHIP',
      scheduleTitle: 'Agendar Portabilidad',
      scheduleSubtitlePrefix: 'Trae tu número a',
      scheduleSubtitleFallbackBrand: 'nuestra operadora',
      smartControlTitle: 'Control  Inteligente',
      smartControlSubtitle: 'Configura tu contrato',
      doubts: '¿Dudas?',
      doubtsSubtitle: 'Atención 24h por WhatsApp',
    },
    smartControlModal: {
      title: 'CONTROL INTELIGENTE',
      done: 'LISTO',
    },
  },
  page4: {
    downloadApp: '¡Descarga nuestra app para enterarte de todo!',
    slogan1: 'Todo listo,',
    slogan2: 'ya puedes',
    slogan3: 'disfrutar tu cuenta',
    sloganVirtual1: 'Recibirás un',
    sloganVirtual2: 'SMS',
    sloganVirtual3: 'para configurar tu plan',
  },
  footer: {
    titleDefault: '#Simplifica con',
    aboutPrefix: 'SOBRE',
    askDoubts: 'RESUELVE TUS DUDAS',
    lgpd: 'PRIVACIDAD',
    contact: 'HABLA CON NOSOTROS',
    copyrightPrefix: '© Grupo',
    copyrightFallbackBrand: 'Contel',
    copyrightSuffix: 'Todos los derechos reservados.',
  },
};

export default es;
